import { useState } from "react";
import { useTranslation } from "react-i18next";
import { exportListAPI } from "../api/api";
import { handleExportResponse } from "../utils/export";
import SuccessMessage from "../component/customComponent/customMessages/SuccessMessage";
import ErrorMessage from "../component/customComponent/customMessages/ErrorMessage";
import {
  useStandardizedTracking,
  EventNames,
  FeatureGroups,
  LayoutTypes,
  ExportActionStatus
} from "../utils/pendo/useStandardizedTracking";

export const useExportWithTracking = () => {
  const { t } = useTranslation();
  const { track } = useStandardizedTracking();
  const [isExporting, setIsExporting] = useState(false);

  /**
   * Helper function to format sort order for tracking
   * Returns object with columnKey and order in a clear structure
   */
  const formatSortOrder = (sorting) => {
    if (!sorting || !sorting.columnKey || !sorting.order) {
      return { columnKey: null, order: null };
    }
    return {
      columnKey: sorting.columnKey,
      order: sorting.order
    };
  };

  /**
   * Helper function to create tracking metadata as JSON string
   */
  const createTrackingInfo = (trackingMetadata, status, duration) => {
    return JSON.stringify({
      listName: trackingMetadata.listName,
      export_type: trackingMetadata.exportType,
      number_records_downloaded: trackingMetadata.numberOfRecords,
      sort_order: formatSortOrder(trackingMetadata.sorting),
      filters_applied: trackingMetadata.filters,
      columns_visible: trackingMetadata.visibleColumns,
      is_saved_list: trackingMetadata.isSavedList || false,
      saved_list_id: trackingMetadata.savedListId || null,
      saved_list_name: trackingMetadata.savedListName || null,
      saved_list_origin_bucket: trackingMetadata.savedListOriginBucket || null,
      status,
      duration: `${duration}s`
    });
  };

  const exportWithTracking = async (exportParams, trackingMetadata) => {
    setIsExporting(true);
    const startTime = Date.now();

    try {
      // Execute export
      const response = await exportListAPI(exportParams);

      // Download file and show success message
      handleExportResponse(
        response,
        () => {
          SuccessMessage(t("export.download_started"));

          // Track successful export
          const duration = (Date.now() - startTime) / 1000;
          try {
            track(EventNames.BUTTON_CLICK, {
              featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
              feature: FeatureGroups.EXPORT_ACTION,
              pageKey: trackingMetadata.pageKey,
              contentType: trackingMetadata.contentType,
              contentKey: trackingMetadata.contentKey,
              layout: LayoutTypes.TABLE,
              additionalInfo: createTrackingInfo(trackingMetadata, ExportActionStatus.SUCCESS, duration)
            });
          } catch (trackingError) {
            console.error("Export tracking error:", trackingError);
          }
        },
        () => {
          ErrorMessage(t("export.error_message"));

          // Track failed export
          const duration = (Date.now() - startTime) / 1000;
          try {
            track(EventNames.BUTTON_CLICK, {
              featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
              feature: FeatureGroups.EXPORT_ACTION,
              pageKey: trackingMetadata.pageKey,
              contentType: trackingMetadata.contentType,
              contentKey: trackingMetadata.contentKey,
              layout: LayoutTypes.TABLE,
              additionalInfo: createTrackingInfo(trackingMetadata, ExportActionStatus.FAILURE, duration)
            });
          } catch (trackingError) {
            console.error("Export tracking error:", trackingError);
          }
        }
      );

      return { success: true, response };
    } catch (error) {
      ErrorMessage(t("export.error_message"));

      // Track failed export
      const duration = (Date.now() - startTime) / 1000;
      try {
        track(EventNames.BUTTON_CLICK, {
          featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
          feature: FeatureGroups.EXPORT_ACTION,
          pageKey: trackingMetadata.pageKey,
          contentType: trackingMetadata.contentType,
          contentKey: trackingMetadata.contentKey,
          layout: LayoutTypes.TABLE,
          additionalInfo: createTrackingInfo(trackingMetadata, ExportActionStatus.FAILURE, duration)
        });
      } catch (trackingError) {
        // do nothing if tracking fails
      }

      return { success: false, error };
    } finally {
      setIsExporting(false);
    }
  };

  return { exportWithTracking, isExporting };
};
